import React, { Fragment, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  APP_BRAND,
  CATEGORY_ORDER,
  FOOD_ITEMS,
  ITEM_BY_ID,
  OCCASIONS,
  PACKAGE_PRESETS,
  sumPerPlateForIds
} from './catalog';
import { getDiscount, totalForGuests, formatINR, bulkSubtotal } from './pricing';
import { useCateringPlannerStore } from './store';
import { encodePlannerShare, decodePlannerShare } from './shareCodec';
import { formatDateDisplay } from '../utils/formatDate';
import './CateringPlannerPage.css';

const MOBILE_MQ = '(max-width: 1023px)';

const CATEGORY_SHORT = {
  'Starters / Appetizers': 'Starters',
  Soups: 'Soups',
  'Main Course (Veg)': 'Main (Veg)',
  'Main Course (Non-Veg)': 'Main (Non)',
  'Breads / Rice / Biryani': 'Breads & rice',
  Accompaniments: 'Sides',
  Desserts: 'Desserts',
  'Beverages / Welcome Drinks': 'Drinks',
  'Live Counters': 'Live'
};

function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const fn = () => setNarrow(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return narrow;
}

function CartBody({ onLineRemove }) {
  const selectedIds = useCateringPlannerStore((s) => s.selectedIds);
  const guestCount = useCateringPlannerStore((s) => s.guestCount);
  const occasion = useCateringPlannerStore((s) => s.occasion);
  const setGuestCount = useCateringPlannerStore((s) => s.setGuestCount);
  const clearCart = useCateringPlannerStore((s) => s.clearCart);
  const applyOccasionSuggestions = useCateringPlannerStore((s) => s.applyOccasionSuggestions);

  const lines = useMemo(
    () => selectedIds.map((id) => ITEM_BY_ID[id]).filter(Boolean),
    [selectedIds]
  );

  const occLabel = OCCASIONS.find((o) => o.value === occasion)?.label || occasion;

  return (
    <>
      <h2>Your menu</h2>
      <p className="cp-muted" style={{ margin: '0 0 8px' }}>
        Occasion: <strong style={{ color: 'var(--cb-maroon)' }}>{occLabel}</strong>
      </p>
      {lines.length === 0 ? (
        <p className="cp-muted" style={{ fontSize: 14 }}>
          Tap dishes to build your plate. Item prices are hidden here — your quote appears on the summary screen when you are ready.
        </p>
      ) : (
        <div>
          {lines.map((item) => (
            <div key={item.id} className="cp-cart-line">
              <span>
                {item.name}
                <span className="cp-muted" style={{ fontSize: 12, marginLeft: 6 }}>
                  ({item.isVeg ? 'Veg' : 'Non-veg'})
                </span>
              </span>
              {onLineRemove && (
                <button type="button" aria-label={`Remove ${item.name}`} onClick={() => onLineRemove(item.id)}>
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="cp-cart-quote-hint">Totals for your guest count are shown only on the <strong>summary screen</strong> (no item prices while you browse).</p>

      <div className="cp-guest-input-row">
        <label htmlFor="cp-guests">Custom guests</label>
        <input
          id="cp-guests"
          type="number"
          min={1}
          max={50000}
          value={guestCount}
          onChange={(e) => setGuestCount(e.target.value)}
        />
      </div>

      <div className="cp-actions-row">
        <button type="button" className="cp-btn cp-btn-secondary" onClick={() => applyOccasionSuggestions()} disabled={occasion === 'custom'}>
          Apply occasion ideas
        </button>
        <button
          type="button"
          className="cp-btn cp-btn-secondary"
          onClick={() => {
            if (!lines.length) return;
            if (!window.confirm('Clear entire menu?')) return;
            clearCart();
          }}
        >
          Clear all
        </button>
      </div>
    </>
  );
}

function MobileStepper({ step }) {
  const steps = [
    { id: 'details', label: 'Details' },
    { id: 'menu', label: 'Menu' },
    { id: 'review', label: 'Review' }
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <nav className="cp-stepper" aria-label="Planning steps">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <Fragment key={s.id}>
            {i > 0 ? <span className="cp-stepper-line" aria-hidden /> : null}
            <div className={`cp-stepper-step${done ? ' done' : ''}${active ? ' active' : ''}`}>
              <span className="cp-stepper-dot">{done ? '✓' : i + 1}</span>
              <span className="cp-stepper-label">{s.label}</span>
            </div>
          </Fragment>
        );
      })}
    </nav>
  );
}

function DishThumb({ item }) {
  const ch = item.name.charAt(0).toUpperCase();
  return (
    <div className={`cp-dish-thumb${item.isVeg ? ' cp-dish-thumb--veg' : ' cp-dish-thumb--nonveg'}`} aria-hidden>
      <span>{ch}</span>
    </div>
  );
}

function mealPreferenceLabel(m) {
  if (m === 'veg') return 'Vegetarian only';
  if (m === 'nonveg') return 'Non-vegetarian selections';
  return 'Vegetarian & non-vegetarian';
}

function formatEventDateDisplay(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const formatted = formatDateDisplay(iso);
  return formatted === '—' ? iso : formatted;
}

function MenuSummaryScreen({
  open,
  onClose,
  onToast,
  lines,
  occLabel,
  guestCount,
  eventDate,
  venue,
  mealPreference,
  grand,
  sub,
  disc,
  savings,
  perGuest
}) {
  const sheetRef = useRef(null);
  const shareBusyRef = useRef(false);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const shareAsLongImage = useCallback(async () => {
    const el = sheetRef.current;
    if (!el || shareBusyRef.current) return;
    shareBusyRef.current = true;
    setShareBusy(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#fff9f0',
        logging: false,
        imageTimeout: 15000
      });
      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png', 0.95);
      });
      if (!blob) {
        onToast?.('Could not build image — try again or use a screenshot');
        return;
      }
      const file = new File([blob], `CaterCraft-menu-${guestCount}guests.png`, { type: 'image/png' });
      const shareData = { files: [file], title: 'CaterCraft menu', text: 'Our CaterCraft menu summary' };
      if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        onToast?.('Choose WhatsApp (or another app) to send the image');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
        onToast?.('Image downloaded — open WhatsApp and attach it from your gallery/files');
      }
    } catch (err) {
      if (err && err.name === 'AbortError') {
        onToast?.('Share cancelled');
      } else {
        onToast?.('Could not share image — try screenshot or download');
      }
    } finally {
      shareBusyRef.current = false;
      setShareBusy(false);
    }
  }, [guestCount, onToast]);

  if (!open) return null;

  const dateLabel = formatEventDateDisplay(eventDate);

  return (
    <div className="cp-summary-overlay" role="dialog" aria-modal="true" aria-labelledby="cp-summary-title" onClick={onClose}>
      <div className="cp-summary-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="cp-summary-sheet" ref={sheetRef}>
          <div className="cp-summary-hero">
            <img src="/cakexpress-logo.png" alt="" className="cp-summary-logo" />
            <h1 id="cp-summary-title" className="cp-summary-title">
              {APP_BRAND.name}
            </h1>
            <p className="cp-summary-tag">{APP_BRAND.subline}</p>
            <div className="cp-summary-hero-gold" aria-hidden />
          </div>

          <section className="cp-summary-block">
            <h2 className="cp-summary-h2">Your event</h2>
            <dl className="cp-summary-dl">
              <div>
                <dt>Occasion</dt>
                <dd>{occLabel}</dd>
              </div>
              {dateLabel ? (
                <div>
                  <dt>Date</dt>
                  <dd>{dateLabel}</dd>
                </div>
              ) : null}
              {venue ? (
                <div>
                  <dt>Venue</dt>
                  <dd>{venue}</dd>
                </div>
              ) : null}
              <div>
                <dt>Guests</dt>
                <dd>{guestCount}</dd>
              </div>
              <div>
                <dt>Meal preference</dt>
                <dd>{mealPreferenceLabel(mealPreference)}</dd>
              </div>
            </dl>
          </section>

          <section className="cp-summary-block">
            <h2 className="cp-summary-h2">Selected menu ({lines.length} dishes)</h2>
            <ul className="cp-summary-dishes">
              {lines.map((item) => (
                <li key={item.id}>
                  <span className="cp-summary-dish-name">{item.name}</span>
                  <span className={`cp-summary-diet ${item.isVeg ? 'veg' : 'nonveg'}`}>{item.isVeg ? 'Veg' : 'Non-veg'}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="cp-summary-block cp-summary-block--quote">
            <h2 className="cp-summary-h2">Indicative quote</h2>
            <div className="cp-summary-quote-rows">
              <div className="cp-summary-quote-row">
                <span>Subtotal ({guestCount} guests)</span>
                <span>{formatINR(sub)}</span>
              </div>
              {disc > 0 ? (
                <div className="cp-summary-quote-row cp-summary-quote-row--muted">
                  <span>Bulk savings ({Math.round(disc * 100)}%)</span>
                  <span>−{formatINR(savings)}</span>
                </div>
              ) : null}
              <div className="cp-summary-quote-row cp-summary-quote-total">
                <span>Grand total</span>
                <span>{formatINR(grand)}</span>
              </div>
            </div>
            <p className="cp-summary-perguest">
              Per guest (indicative): <strong>{formatINR(perGuest)}</strong>
            </p>
            <p className="cp-summary-legal">Final pricing confirmed with CaterCraft. Discounts apply at 200+ and 500+ guests.</p>
          </section>
        </div>
      </div>
      <div className="cp-summary-toolbar" onClick={(e) => e.stopPropagation()}>
        <p className="cp-summary-hint">
          <strong>Share as image</strong> creates one tall PNG — your phone&apos;s share sheet lets you pick WhatsApp. Or use a screenshot.
        </p>
        <div className="cp-summary-toolbar-btns">
          <button
            type="button"
            className="cp-btn cp-btn-secondary cp-summary-share"
            disabled={shareBusy}
            onClick={shareAsLongImage}
          >
            {shareBusy ? 'Preparing image…' : 'Share as image'}
          </button>
          <button type="button" className="cp-btn cp-btn-primary cp-summary-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CateringPlannerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [toast, setToast] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const isNarrow = useIsNarrow();
  const [mobileStep, setMobileStep] = useState('details');
  const [menuSearch, setMenuSearch] = useState('');
  const [mobileMenuCategory, setMobileMenuCategory] = useState('all');
  const wasNarrowRef = useRef(false);
  const layoutMountedRef = useRef(false);

  const activeCategory = useCateringPlannerStore((s) => s.activeCategory);
  const setActiveCategory = useCateringPlannerStore((s) => s.setActiveCategory);
  const selectedIds = useCateringPlannerStore((s) => s.selectedIds);
  const occasion = useCateringPlannerStore((s) => s.occasion);
  const setOccasion = useCateringPlannerStore((s) => s.setOccasion);
  const guestCount = useCateringPlannerStore((s) => s.guestCount);
  const eventDate = useCateringPlannerStore((s) => s.eventDate);
  const venue = useCateringPlannerStore((s) => s.venue);
  const mealPreference = useCateringPlannerStore((s) => s.mealPreference);
  const setEventDate = useCateringPlannerStore((s) => s.setEventDate);
  const setVenue = useCateringPlannerStore((s) => s.setVenue);
  const setMealPreference = useCateringPlannerStore((s) => s.setMealPreference);
  const bumpGuestCount = useCateringPlannerStore((s) => s.bumpGuestCount);
  const toggleItem = useCateringPlannerStore((s) => s.toggleItem);
  const applyPackage = useCateringPlannerStore((s) => s.applyPackage);
  const hydrateFromShare = useCateringPlannerStore((s) => s.hydrateFromShare);

  const perPlate = useMemo(() => sumPerPlateForIds(selectedIds), [selectedIds]);
  const disc = getDiscount(guestCount);
  const sub = bulkSubtotal(perPlate, guestCount);
  const grand = totalForGuests(perPlate, guestCount);
  const savings = sub - grand;

  useEffect(() => {
    const c = searchParams.get('c');
    if (!c) return;
    const payload = decodePlannerShare(c);
    if (payload?.s) {
      hydrateFromShare(payload);
      setSearchParams({}, { replace: true });
      if (typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches) {
        setMobileStep('menu');
      }
    }
  }, [searchParams, hydrateFromShare, setSearchParams]);

  useEffect(() => {
    if (!layoutMountedRef.current) {
      layoutMountedRef.current = true;
      wasNarrowRef.current = isNarrow;
      return;
    }
    if (isNarrow && !wasNarrowRef.current) setMobileStep('details');
    wasNarrowRef.current = isNarrow;
  }, [isNarrow]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  }, []);

  const itemsInCategory = useMemo(
    () => FOOD_ITEMS.filter((i) => i.category === activeCategory),
    [activeCategory]
  );

  const mobileFilteredItems = useMemo(() => {
    let list = FOOD_ITEMS;
    if (mealPreference === 'veg') list = list.filter((i) => i.isVeg);
    if (mealPreference === 'nonveg') list = list.filter((i) => !i.isVeg);
    if (mobileMenuCategory !== 'all') {
      list = list.filter((i) => i.category === mobileMenuCategory);
    }
    const q = menuSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.description && i.description.toLowerCase().includes(q)) ||
          (i.tags && i.tags.some((t) => t.toLowerCase().includes(q)))
      );
    }
    return list;
  }, [mealPreference, mobileMenuCategory, menuSearch]);

  const handlePackage = (key) => {
    if (selectedIds.length && !window.confirm('Replace your current menu with this package?')) return;
    applyPackage(key);
    showToast(`${PACKAGE_PRESETS[key].label} loaded`);
  };

  const shareMenu = () => {
    const enc = encodePlannerShare({
      selectedIds,
      guestCount,
      occasion
    });
    const url = `${window.location.origin}/catering-planner?c=${encodeURIComponent(enc)}`;
    navigator.clipboard.writeText(url).then(
      () => showToast('Share link copied'),
      () => window.prompt('Copy link:', url)
    );
  };

  const openSummaryScreen = useCallback(() => {
    if (!selectedIds.length) {
      showToast('Pick at least one dish for your plate first');
      return;
    }
    setSummaryOpen(true);
  }, [selectedIds.length, showToast]);

  const closeSummaryScreen = useCallback(() => setSummaryOpen(false), []);

  const occLabel = OCCASIONS.find((o) => o.value === occasion)?.label || occasion;
  const printLines = selectedIds.map((id) => ITEM_BY_ID[id]).filter(Boolean);
  const perGuest = guestCount > 0 ? grand / guestCount : 0;

  const flowTitle =
    mobileStep === 'details' ? 'Plan your event' : mobileStep === 'menu' ? 'Select your menu' : 'Review your menu';
  const flowSub =
    mobileStep === 'details'
      ? 'Tell us about your occasion'
      : `${guestCount} guests · ${occLabel}`;

  return (
    <div className={`cp-root${isNarrow ? ' cp-root--mobile-flow' : ''}`}>
      <div className="cp-content-shell">
        <header className="cp-header cp-no-print">
          <div className={`cp-header-inner${isNarrow ? ' cp-header-inner--flow' : ''}`}>
            <div className="cp-brand-row">
              <div className="cp-brand">
                <img src="/cakexpress-logo.png" alt="" className="cp-logo" />
                {isNarrow ? (
                  <>
                    <h1 className="cp-flow-title">{flowTitle}</h1>
                    <p className="cp-flow-sub">{flowSub}</p>
                  </>
                ) : (
                  <>
                    <h1>{APP_BRAND.name}</h1>
                    {APP_BRAND.subline ? <p className="cp-brand-tagline">{APP_BRAND.subline}</p> : null}
                    <p>{APP_BRAND.tagline}</p>
                  </>
                )}
              </div>
              {!isNarrow ? (
                <div className="cp-header-tools">
                  <select className="cp-select" value={occasion} onChange={(e) => setOccasion(e.target.value)} aria-label="Occasion">
                    {OCCASIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <Link to="/login" className="cp-link-staff">
                    Staff login →
                  </Link>
                </div>
              ) : (
                <Link to="/login" className="cp-link-staff cp-link-staff--compact">
                  Staff
                </Link>
              )}
            </div>

            {!isNarrow ? (
              <div className="cp-packages">
                {Object.values(PACKAGE_PRESETS).map((pkg) => (
                  <button key={pkg.key} type="button" className="cp-package-card" onClick={() => handlePackage(pkg.key)}>
                    <h3>{pkg.label}</h3>
                    <div className="tag">{pkg.tagline}</div>
                    <p>{pkg.blurb}</p>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="cp-garland-rule cp-garland-rule--header cp-no-print" aria-hidden="true" />
        </header>

        {isNarrow && mobileStep !== 'details' ? (
          <div className="cp-stepper-wrap cp-no-print">
            <MobileStepper step={mobileStep} />
          </div>
        ) : null}

        {isNarrow && mobileStep === 'details' ? (
          <section className="cp-mobile-details cp-no-print" aria-label="Event details">
            <div className="cp-mobile-details-card">
              <div className="cp-mobile-details-ornament" aria-hidden>
                <span />
                <span />
                <span />
              </div>
              <label className="cp-mobile-field-label">Event type</label>
              <select className="cp-mobile-field" value={occasion} onChange={(e) => setOccasion(e.target.value)} aria-label="Event type">
                {OCCASIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <label className="cp-mobile-field-label">Event date</label>
              <input className="cp-mobile-field" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />

              <label className="cp-mobile-field-label">Venue / location</label>
              <input
                className="cp-mobile-field"
                type="text"
                placeholder="Venue name, city"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                autoComplete="street-address"
              />

              <label className="cp-mobile-field-label">Number of guests</label>
              <div className="cp-mobile-guest-stepper">
                <button type="button" className="cp-guest-step-btn" onClick={() => bumpGuestCount(-1)} aria-label="Fewer guests">
                  −
                </button>
                <span className="cp-guest-step-value">{guestCount}</span>
                <button type="button" className="cp-guest-step-btn" onClick={() => bumpGuestCount(1)} aria-label="More guests">
                  +
                </button>
              </div>

              <label className="cp-mobile-field-label">Meal preference</label>
              <div className="cp-meal-prefs" role="group" aria-label="Meal preference">
                <button
                  type="button"
                  className={`cp-meal-pref${mealPreference === 'veg' ? ' on' : ''}`}
                  onClick={() => setMealPreference('veg')}
                >
                  <span className="cp-meal-pref-ico" aria-hidden>
                    🌿
                  </span>
                  Veg only
                </button>
                <button
                  type="button"
                  className={`cp-meal-pref${mealPreference === 'nonveg' ? ' on' : ''}`}
                  onClick={() => setMealPreference('nonveg')}
                >
                  Non-veg
                </button>
                <button
                  type="button"
                  className={`cp-meal-pref${mealPreference === 'both' ? ' on' : ''}`}
                  onClick={() => setMealPreference('both')}
                >
                  Both
                </button>
              </div>

              <p className="cp-mobile-details-hint">Packages load curated dishes — use them on the next step from quick picks if you like.</p>
              <div className="cp-mobile-package-row">
                {Object.values(PACKAGE_PRESETS).map((pkg) => (
                  <button key={pkg.key} type="button" className="cp-mobile-package-chip" onClick={() => handlePackage(pkg.key)}>
                    {pkg.label.replace(' Package', '')}
                  </button>
                ))}
              </div>
            </div>

            <div className="cp-mobile-sticky-footer cp-mobile-sticky-footer--cream">
              <button type="button" className="cp-btn cp-btn-primary cp-mobile-cta" onClick={() => setMobileStep('menu')}>
                Continue to menu →
              </button>
            </div>
          </section>
        ) : null}

        {!(isNarrow && mobileStep === 'details') ? (
          <div className="cp-layout">
            <main className="cp-main cp-no-print">
              {!isNarrow ? (
                <>
                  <div className="cp-tabs" role="tablist" aria-label="Menu category">
                    {CATEGORY_ORDER.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        role="tab"
                        aria-selected={activeCategory === cat}
                        className={`cp-tab${activeCategory === cat ? ' active' : ''}`}
                        onClick={() => setActiveCategory(cat)}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <div className="cp-grid">
                    {itemsInCategory.map((item) => {
                      const on = selectedIds.includes(item.id);
                      return (
                        <div key={item.id} className="cp-card-wrap">
                          <article className={`cp-card${on ? ' selected' : ''}`}>
                            {on && (
                              <span className="cp-badge" aria-hidden>
                                ✓
                              </span>
                            )}
                            <div className="cp-card-top">
                              <h3 className="cp-card-title">{item.name}</h3>
                              <span className={`cp-diet ${item.isVeg ? 'veg' : 'nonveg'}`} title={item.isVeg ? 'Vegetarian' : 'Non-vegetarian'} />
                            </div>
                            {item.description ? <p className="cp-card-desc">{item.description}</p> : null}
                            {item.tags?.length ? (
                              <div className="cp-tags">
                                {item.tags.map((t) => (
                                  <span key={t} className="cp-tag">
                                    {t}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <div className="cp-card-foot">
                              <span className="cp-card-foot-note">On your plate</span>
                              <button type="button" className={`cp-add${on ? ' on' : ''}`} onClick={() => toggleItem(item.id)}>
                                {on ? 'Added' : 'Add'}
                              </button>
                            </div>
                          </article>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}

              {isNarrow && mobileStep === 'menu' ? (
                <div className="cp-mobile-menu">
                  <div className="cp-mobile-search-wrap">
                    <span className="cp-mobile-search-ico" aria-hidden>
                      ◎
                    </span>
                    <input
                      type="search"
                      className="cp-mobile-search"
                      placeholder="Search dishes…"
                      value={menuSearch}
                      onChange={(e) => setMenuSearch(e.target.value)}
                      aria-label="Search dishes"
                    />
                  </div>

                  <div className="cp-mobile-chips" role="tablist" aria-label="Course filter">
                    <button
                      type="button"
                      className={`cp-mobile-chip${mobileMenuCategory === 'all' ? ' active' : ''}`}
                      onClick={() => {
                        setMobileMenuCategory('all');
                        setActiveCategory(CATEGORY_ORDER[0]);
                      }}
                    >
                      All
                    </button>
                    {CATEGORY_ORDER.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        className={`cp-mobile-chip${mobileMenuCategory === cat ? ' active' : ''}`}
                        onClick={() => {
                          setMobileMenuCategory(cat);
                          setActiveCategory(cat);
                        }}
                      >
                        {CATEGORY_SHORT[cat] || cat}
                      </button>
                    ))}
                  </div>

                  <div className="cp-mobile-menu-groups">
                    {CATEGORY_ORDER.map((cat) => {
                      const inCat = mobileFilteredItems.filter((i) => i.category === cat);
                      if (!inCat.length) return null;
                      return (
                        <section key={cat} className="cp-mobile-menu-group">
                          <h2 className="cp-mobile-group-title">{cat.toUpperCase()}</h2>
                          <ul className="cp-mobile-dish-list">
                            {inCat.map((item) => {
                              const on = selectedIds.includes(item.id);
                              return (
                                <li key={item.id} className={`cp-menu-row${on ? ' cp-menu-row--on' : ''}`}>
                                  <DishThumb item={item} />
                                  <div className="cp-menu-row-body">
                                    <div className="cp-menu-row-title">
                                      <span>{item.name}</span>
                                      <span className={`cp-diet ${item.isVeg ? 'veg' : 'nonveg'}`} title={item.isVeg ? 'Veg' : 'Non-veg'} />
                                    </div>
                                    {item.description ? <p className="cp-menu-row-desc">{item.description}</p> : null}
                                  </div>
                                  <button
                                    type="button"
                                    className={`cp-menu-row-add${on ? ' on' : ''}`}
                                    onClick={() => toggleItem(item.id)}
                                    aria-pressed={on}
                                    aria-label={on ? `Remove ${item.name}` : `Add ${item.name}`}
                                  >
                                    {on ? '✓' : '+'}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </section>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {isNarrow && mobileStep === 'review' ? (
                <div className="cp-mobile-review">
                  <h2 className="cp-mobile-group-title">Selected dishes ({selectedIds.length})</h2>
                  <ul className="cp-mobile-review-list">
                    {printLines.map((item) => (
                      <li key={item.id} className="cp-review-line">
                        <DishThumb item={item} />
                        <div className="cp-review-line-body">
                          <strong>{item.name}</strong>
                          <span className="cp-review-line-meta">On your plate · {guestCount} guests</span>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <p className="cp-mobile-review-hint">
                    Open the summary screen to see your indicative total for {guestCount} guests — perfect for a screenshot to share.
                  </p>
                  <button type="button" className="cp-btn cp-btn-primary cp-mobile-review-summary-btn" onClick={openSummaryScreen}>
                    View summary screen
                  </button>
                </div>
              ) : null}
            </main>

            <aside className="cp-aside cp-aside-desktop cp-no-print" aria-label="Menu summary">
              <CartBody onLineRemove={toggleItem} />
              <div className="cp-actions-row">
                <button type="button" className="cp-btn cp-btn-primary" onClick={openSummaryScreen}>
                  View summary screen
                </button>
                <button type="button" className="cp-btn cp-btn-secondary" onClick={shareMenu}>
                  Share menu
                </button>
              </div>
            </aside>
          </div>
        ) : null}

        <p className={`cp-footer-blessing cp-no-print${isNarrow ? ' cp-footer-blessing--tight' : ''}`}>
          <span>✦</span>
          Wishing you a joyful gathering and a memorable feast
          <span>✦</span>
        </p>

        {isNarrow && mobileStep === 'menu' ? (
          <footer className="cp-mobile-sticky-footer cp-mobile-sticky-footer--maroon cp-no-print">
            <div className="cp-mobile-sticky-maroon-top" aria-hidden />
            <div className="cp-mobile-sticky-maroon-inner">
              <div>
                <div className="cp-mobile-sticky-label">{selectedIds.length} dishes on your plate</div>
                <div className="cp-mobile-sticky-sub">Totals on summary screen</div>
              </div>
              <button type="button" className="cp-btn cp-btn-accent" onClick={() => setMobileStep('review')}>
                Review menu →
              </button>
            </div>
          </footer>
        ) : null}

        {isNarrow && mobileStep === 'review' ? (
          <footer className="cp-mobile-sticky-footer cp-mobile-sticky-footer--review cp-no-print">
            <button type="button" className="cp-btn cp-btn-linkish" onClick={() => setMobileStep('menu')}>
              ← Edit menu
            </button>
            <div className="cp-mobile-review-actions">
              <button type="button" className="cp-btn cp-btn-secondary" onClick={shareMenu}>
                Share
              </button>
              <button type="button" className="cp-btn cp-btn-primary" onClick={openSummaryScreen}>
                View summary →
              </button>
            </div>
          </footer>
        ) : null}

        <MenuSummaryScreen
          open={summaryOpen}
          onClose={closeSummaryScreen}
          onToast={showToast}
          lines={printLines}
          occLabel={occLabel}
          guestCount={guestCount}
          eventDate={eventDate}
          venue={venue}
          mealPreference={mealPreference}
          grand={grand}
          sub={sub}
          disc={disc}
          savings={savings}
          perGuest={perGuest}
        />

        {toast ? <div className="cp-toast">{toast}</div> : null}
      </div>
    </div>
  );
}
